import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BookOpen, CalendarDays } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export default function BlogPage() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/blog").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  return (
    <div data-testid="blog-page" className="min-h-screen bg-ink text-foreground">
      <Seo
        title="Blog — AB Hunter Consulting USA"
        siteName="AB Hunter Consulting USA"
        description="Stories, insights and updates on leadership, mentorship and community from AB Hunter Consulting USA."
      />
      <div className="relative mx-auto max-w-5xl px-5 pb-24 pt-24 sm:px-8 lg:pt-16">
        <Link to="/" data-testid="blog-back-home" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-gold">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Stories & Insights</p>
        <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">The Blog</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          Notes on leadership, mentorship and community — plus news from AB Hunter Consulting USA.
        </p>

        {posts === null ? (
          <div className="mt-16 grid gap-6 sm:grid-cols-2">
            {[0, 1].map((i) => <div key={i} className="h-64 animate-pulse rounded-3xl border border-gold/10 bg-ink-card/50" />)}
          </div>
        ) : posts.length === 0 ? (
          <div data-testid="blog-empty" className="mt-16 rounded-3xl border border-gold/15 bg-ink-card/50 p-10 text-center">
            <BookOpen className="mx-auto h-8 w-8 text-gold" />
            <p className="mt-4 font-display text-xl font-semibold">No posts yet</p>
            <p className="mt-2 text-sm text-muted-foreground">The first story is being written — check back soon.</p>
          </div>
        ) : (
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {posts.map((post, i) => (
              <motion.div key={post.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, duration: 0.5 }}>
                <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-gold/15 bg-ink-card/60 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-[0_20px_40px_-20px_rgba(212,175,55,0.25)]">
                  {post.image && (
                    <div className="h-48 overflow-hidden">
                      <img src={post.image} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                      <CalendarDays className="h-3.5 w-3.5" /> {fmtDate(post.created_at)}
                    </p>
                    <h2 className="mt-3 font-display text-xl font-semibold leading-snug group-hover:text-gold">{post.title}</h2>
                    {post.seo_description && (
                      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">{post.seo_description}</p>
                    )}
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
                      Read <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
